import React from 'react';
import { FormattedMessage } from 'react-intl';

import EventsHistoryReloadButton from '@/components/mui/Layouts/components/EventsHistoryPanel/components/ReloadButton';
import EventsHistoryListItem from '@/components/mui/Layouts/components/EventsHistoryPanel/components/EventsList/ListItem';
import { EventsListWrapper } from '@/components/mui/Layouts/components/EventsHistoryPanel/components/EventsList/StyledComponents';
import List from '@mui/material/List';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import elements from '@/components/elements';

export interface EventsHistoryEntry {
  id: string;
  userId?: string | null;
  username?: string | null;
  action?: (() => void) | null;
}

interface EventsHistoryListProps {
  items: ReadonlyArray<EventsHistoryEntry>;
  getAvatarSrc: (entry: EventsHistoryEntry) => string | undefined;
  onAction: (entry: EventsHistoryEntry) => void;
  getNextEvents: () => void;
  onReload: () => void;
  isNext: string | null | undefined;
  counter: number;
  dateFormat: string;
  isError?: boolean;
}

function isSameItems(a: ReadonlyArray<EventsHistoryEntry>, b: ReadonlyArray<EventsHistoryEntry>): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((entry, i) => entry.id === b[i].id);
}

class EventsHistoryList extends React.Component<EventsHistoryListProps> {
  static displayName = 'EventsList';

  isIntersecting = false;

  observer: IntersectionObserver | null = null;

  container: HTMLElement | null = null;

  componentDidMount() {
    if (this.observer && this.container) {
      this.observer.observe(this.container);
    }
  }

  shouldComponentUpdate(nextProps: EventsHistoryListProps) {
    if (!isSameItems(nextProps.items, this.props.items)) return true;
    if (this.props.counter !== nextProps.counter) return true;
    if (this.props.isNext !== nextProps.isNext) return true;
    if (this.props.getAvatarSrc !== nextProps.getAvatarSrc) return true;
    if (this.props.dateFormat !== nextProps.dateFormat) return true;
    if (this.props.isError !== nextProps.isError) return true;
    if (this.props.onAction !== nextProps.onAction) return true;
    if (this.props.onReload !== nextProps.onReload) return true;
    return false;
  }

  componentDidUpdate(prevProps: EventsHistoryListProps) {
    const { isNext } = this.props;
    if (isNext === null && this.observer && this.container) {
      this.observer.unobserve(this.container);
    }
    if (typeof isNext === 'string' && this.isIntersecting) {
      this.props.getNextEvents();
    }
    // aftter date selection isNext is undefined and observer must be
    // inittialized in this case as if it were null
    if ((prevProps.isNext === null || prevProps.isNext === undefined) && typeof isNext === 'string') {
      if (this.observer && this.container) {
        this.observer.observe(this.container);
      }
    }
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  get hasNext() {
    return this.props.isNext !== null;
  }

  getNext = (entries: IntersectionObserverEntry[]) => {
    const { isIntersecting, intersectionRect } = entries[0];
    this.isIntersecting = isIntersecting;
    if (isIntersecting && intersectionRect.width > 0) {
      this.props.getNextEvents();
    }
  };

  refProxy = (el: HTMLElement | null) => {
    this.container = el;
    if (el) {
      this.observer = new IntersectionObserver(this.getNext, {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      });
    }
  };

  render() {
    const { isNext, items, getAvatarSrc, onReload, counter, onAction, dateFormat, isError } = this.props;

    return (
      <EventsListWrapper data-element={elements.history.list}>
        <EventsHistoryReloadButton onReload={onReload} counter={counter} />
        <List disablePadding>
          {items.map((entry) => (
            <EventsHistoryListItem
              key={entry.id}
              entry={entry}
              onClick={onAction}
              dateFormat={dateFormat}
              src={getAvatarSrc(entry)}
            />
          ))}
        </List>
        {items.length === 0 && isNext === null && !isError && (
          <Box m="auto" textAlign="center">
            <AssignmentOutlinedIcon />
            <Typography variant="body1">
              <FormattedMessage id="eventsHistory.panel.empty" defaultMessage="No history found" />
            </Typography>
          </Box>
        )}
        <Box ref={this.refProxy} display="flex" justifyContent="center" minHeight="20" py={2}>
          {this.hasNext && <CircularProgress />}
        </Box>
      </EventsListWrapper>
    );
  }
}

export default EventsHistoryList;
